import headerLogo from '../images/header-logo.png';
import {
  LandingPartner,
  NewPartner,
  RecommendedPartner,
} from '../types/partner';
import { Barber } from '../types/barber';
import { Shop } from '../types/shop';
import {
  getBarberById,
  getShopById,
} from '../middleware/registration_service/getRequests';
import { barberTestAccountIdList, shopTestAccountIdList } from '../config';
import { getOpenStatus } from './businessHoursUtils';

const formatLocation = (entity: {
  addressLineOne?: string;
  addressLineTwo?: string;
  city?: string;
  postcode?: string;
}): string =>
  [entity.addressLineOne, entity.addressLineTwo, entity.city, entity.postcode]
    .filter(Boolean)
    .join(', ');

const formatOpeningHours = (entity: {
  openingHours?: string;
  closingHours?: string;
}): string => {
  if (!entity.openingHours && !entity.closingHours) return '';
  return `${entity.openingHours || '--'} - ${entity.closingHours || '--'}`;
};

/**
 * Formats a barber's opening hours for display, preferring their weekly
 * business hours (used by Independent Barbers) and falling back to the
 * legacy single opening/closing time strings when business hours aren't set.
 */
const formatBusinessHours = (barber: Barber): string => {
  const { todayHours } = getOpenStatus(
    barber.businessHours,
    barber.openingHours,
    barber.closingHours
  );
  return todayHours ?? (barber.businessHours?.businessHoursMap ? 'Closed' : formatOpeningHours(barber));
};

const toShopLandingPartner = (
  partnerId: bigint,
  shop: Shop,
  origin: string
): LandingPartner => ({
  id: partnerId.toString(),
  type: 'shop',
  name: shop?.name || '',
  image: shop?.shopProfileImageLink || headerLogo,
  location: formatLocation(shop || {}),
  openingHours: formatOpeningHours(shop || {}),
  profileUrl: `${origin}/shop/${shop.id}`,
});

const toBarberLandingPartner = (
  partnerId: bigint,
  barber: Barber,
  origin: string
): LandingPartner => ({
  id: partnerId.toString(),
  type: 'barber',
  name: `${barber?.firstName || ''}`.trim(),
  image: barber?.profileImageSelfLink || headerLogo,
  location: formatLocation(barber || {}),
  openingHours: formatBusinessHours(barber || {}),
  profileUrl: `${origin}/barber/${partnerId}`,
});

/**
 * Takes the list of new partners returned by getNewPartners and resolves
 * each one's full shop/barber details, mapping the result into the
 * LandingPartner shape consumed by the landing page carousels.
 */
export const getNewToBarbersLimitedData = async (
  newPartners: NewPartner[]
): Promise<LandingPartner[]> => {
  const origin = window.location.origin;

  const partners = await Promise.all(
    newPartners.map(async (partner): Promise<LandingPartner | null> => {
      const partnerId = BigInt(partner.id);

      try {
        if (partner.type === 'SHOP') {
          // Exclude test accounts from the new partners list
          if (shopTestAccountIdList.includes(partnerId)) {
            return null;
          }
          const response = await getShopById(partnerId);
          return toShopLandingPartner(partnerId, response.data, origin);
        }

        // Exclude test accounts from the new partners list
        if (barberTestAccountIdList.includes(partnerId)) {
          return null;
        }
        const response = await getBarberById(partnerId);
        return toBarberLandingPartner(partnerId, response.data, origin);
      } catch (err) {
        console.error(
          `Failed to load ${partner.type} partner ${partnerId}:`,
          err
        );
        return null;
      }
    })
  );

  return partners.filter(
    (partner): partner is LandingPartner => partner !== null
  );
};

/**
 * Takes the list of recommended partners returned by getRecommendedPartners
 * and resolves each one's full shop/barber details, mapping the result into
 * the LandingPartner shape consumed by the landing page carousels.
 */
export const getRecommendedPartnersData = async (
  recommendedPartners: RecommendedPartner[]
): Promise<LandingPartner[]> => {
  const origin = window.location.origin;

  const partners = await Promise.all(
    recommendedPartners.map(async (partner): Promise<LandingPartner | null> => {
      const isShop = partner.shop_id !== null && partner.shop_id !== undefined;
      const id = BigInt((isShop ? partner.shop_id : partner.barber_id)!);

      try {
        // Exclude test accounts from the recommended partners list
        if (isShop) {
          if (shopTestAccountIdList.includes(id)) {
            return null;
          }
          const response = await getShopById(id);
          const shop: Shop = response.data;
          const shopOwnerId: bigint | undefined = shop.shopOwnerId;
          if (!shopOwnerId) {
            console.error(`Shop ${id} does not have a shop owner ID.`);
            return null;
          }
          return toShopLandingPartner(shopOwnerId, response.data, origin);
        }

        // Exclude test accounts from the recommended partners list
        if (barberTestAccountIdList.includes(id)) {
          return null;
        }
        const response = await getBarberById(id);
        return toBarberLandingPartner(id, response.data, origin);
      } catch (err) {
        console.error(`Failed to load recommended partner ${id}:`, err);
        return null;
      }
    })
  );

  return partners.filter(
    (partner): partner is LandingPartner => partner !== null
  );
};
